import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { useSession } from '../../stores/session';
import { NewPlaylist } from './LibraryDock';
import { playlists } from './api';

/**
 * Put whatever is playing on a playlist, from the bar.
 *
 * ## Why it belongs in the dock
 *
 * Same reason favouriting does: the thing playing is the thing you want to
 * keep, and the row you originally clicked may have unmounted several
 * navigations ago. Asking somebody to find the track again to save it is asking
 * them not to bother.
 *
 * ## Making one on the way through
 *
 * The first playlist is the one nobody has. If the answer to "add to which?"
 * is "you have none", the menu has to be able to make one and use it in the
 * same gesture - otherwise the first save is: leave here, go to the library,
 * make a list, come back, find the track again.
 */
export function AddToPlaylist({ trackId, title }: { trackId: number; title: string }) {
  const user = useSession((state) => state.user);
  const client = useQueryClient();

  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [added, setAdded] = useState<string | null>(null);

  const root = useRef<HTMLDivElement | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['playlists', user?.username],
    queryFn: () => playlists.mine(user?.username ?? ''),
    enabled: open && Boolean(user),
  });

  const add = useMutation({
    mutationFn: ({ id }: { id: number; name: string }) => playlists.addTrack(id, trackId),
    onSuccess: (_result, variables) => {
      void client.invalidateQueries({ queryKey: ['playlists', user?.username] });
      void client.invalidateQueries({ queryKey: ['playlist', variables.id] });

      setAdded(variables.name);
      setNaming(false);
    },
  });

  /* Escape, and a click anywhere else. See `ChatLauncher` for the pointerdown. */
  useEffect(() => {
    if (!open) return;

    const away = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };

    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);

    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  /*
   * The confirmation clears itself, and clears when the track changes.
   *
   * Otherwise "Added to Late Nights" sits under the next song, claiming
   * something about it that never happened.
   */
  useEffect(() => {
    setAdded(null);
    setNaming(false);
  }, [trackId]);

  useEffect(() => {
    if (added === null) return;

    const timer = setTimeout(() => setAdded(null), 2600);

    return () => clearTimeout(timer);
  }, [added]);

  if (!user) return null;

  const items = data?.items ?? [];

  return (
    <div className="relative flex items-center" ref={root}>
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-label={`Add ${title} to a playlist`}
        data-add-to-playlist
        className={`rounded-[var(--radius-pill)] px-1.5 py-1 transition-colors ${
          open ? 'text-ink' : 'text-soft hover:text-ink'
        }`}
      >
        <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
          <path d="M7 2h2v5h5v2H9v5H7V9H2V7h5z" />
        </svg>
      </button>

      {open && (
        <div
          data-add-to-playlist-menu
          className="absolute bottom-[calc(100%+10px)] left-1/2 z-20 flex w-[260px] -translate-x-1/2 flex-col overflow-hidden rounded-[var(--radius-panel)] border border-line-lit bg-raised shadow-2xl"
        >
          <div className="border-b border-line px-3 py-2">
            <p className="text-[13px] font-medium text-ink">Add to playlist</p>
            <p className="truncate text-[11px] text-faint">{title}</p>
          </div>

          <div className="max-h-[240px] overflow-y-auto p-1.5">
            {naming ? (
              <NewPlaylist
                onDone={(made) => {
                  if (made === null) {
                    setNaming(false);

                    return;
                  }

                  /*
                   * Straight onto the list it just made, which is what "create
                   * one on the fly and then it goes to it" asks for. A new
                   * empty playlist and a track still not saved would be the
                   * gesture half done.
                   */
                  add.mutate({ id: made.id, name: made.title });
                }}
              />
            ) : (
              <button
                type="button"
                onClick={() => setNaming(true)}
                data-add-new-playlist
                className="flex w-full items-center gap-2 rounded-[var(--radius-control)] px-2 py-2 text-left text-[13px] font-medium text-accent transition-colors hover:bg-slate"
              >
                <span className="font-mono text-sm">+</span>
                New playlist
              </button>
            )}

            {isPending && open && (
              <p className="px-2 py-2 font-mono text-[10px] text-ghost">loading…</p>
            )}

            {items.map((list) => (
              <button
                key={list.id}
                type="button"
                disabled={add.isPending}
                onClick={() => add.mutate({ id: list.id, name: list.title })}
                data-add-to={list.id}
                className="flex w-full items-center gap-2 rounded-[var(--radius-control)] px-2 py-2 text-left transition-colors hover:bg-slate disabled:opacity-50"
              >
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                  {list.title}
                </span>
                <span className="shrink-0 font-mono text-[10px] text-ghost">
                  {list.track_count}
                </span>
              </button>
            ))}
          </div>

          {(added !== null || add.isError) && (
            <p
              data-add-result
              className={`border-t border-line px-3 py-2 text-[11px] ${
                add.isError ? 'text-danger' : 'text-soft'
              }`}
            >
              {add.isError ? 'That did not save. Try again.' : `Added to ${added}.`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
